import React from 'react';
import Card from '../design-system/Card';
import { colors, spacing, radius, fontStack } from '../design-system/tokens';
import { useTheme } from '../hooks/useTheme';

/**
 * /settings — Phase 12.
 * Single section for now: Appearance / Theme.
 * Click any card → applies immediately and persists to localStorage.
 */
export default function Settings() {
  const { theme, setTheme } = useTheme();

  return (
    <div
      style={{
        maxWidth: 800,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: spacing.base,
        fontFamily: fontStack,
      }}
    >
      <header>
        <h1
          style={{
            margin: 0,
            fontSize: 24,
            fontWeight: 600,
            color: colors.textPrimary,
            letterSpacing: '-0.01em',
          }}
        >
          Settings
        </h1>
        <p style={{ margin: '4px 0 0', fontSize: 14, color: colors.textSecondary }}>
          Customize your experience.
        </p>
      </header>

      <Card padding={spacing.lg}>
        <div
          style={{
            fontSize: 12,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: colors.textSecondary,
            fontWeight: 600,
          }}
        >
          Appearance
        </div>
        <h2
          style={{
            margin: '12px 0 4px',
            fontSize: 18,
            fontWeight: 600,
            color: colors.textPrimary,
          }}
        >
          Theme
        </h2>
        <p style={{ margin: 0, fontSize: 14, color: colors.textSecondary }}>
          Choose how Curx looks to you.
        </p>

        <div className="curx-theme-grid">
          {THEMES.map((opt) => (
            <ThemeCard
              key={opt.value}
              option={opt}
              selected={theme === opt.value}
              onClick={() => setTheme(opt.value)}
            />
          ))}
        </div>

        <p
          style={{
            margin: '12px 0 0',
            fontSize: 12,
            color: colors.textSecondary,
          }}
        >
          “System” follows your operating-system preference. It updates automatically when you switch.
        </p>
      </Card>

      <style>{`
        .curx-theme-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          margin-top: 18px;
        }
        @media (max-width: 640px) {
          .curx-theme-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
}

const THEMES = [
  { value: 'light', label: 'Light', preview: 'light' },
  { value: 'dark', label: 'Dark', preview: 'dark' },
  { value: 'system', label: 'System', preview: 'system' },
];

function ThemeCard({ option, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      style={{
        textAlign: 'left',
        padding: 14,
        borderRadius: radius.md,
        border: selected
          ? `2px solid ${colors.primary}`
          : `1px solid ${colors.border}`,
        background: selected ? 'var(--primary-soft)' : colors.card,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        fontFamily: fontStack,
        transition: 'border-color 120ms ease, background-color 120ms ease',
        // Compensate for the thicker selected border so the layout doesn't jump.
        margin: selected ? 0 : 1,
      }}
    >
      <ThemePreview kind={option.preview} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: colors.textPrimary,
          }}
        >
          {option.label}
        </span>
        {selected && (
          <span
            aria-hidden="true"
            style={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              background: colors.primary,
              color: '#fff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            ✓
          </span>
        )}
      </div>
    </button>
  );
}

/**
 * Tiny preview rectangles representing each theme. Light uses a white
 * background with two gray bars. Dark uses a near-black background with
 * lighter bars. System uses a half-and-half split.
 */
function ThemePreview({ kind }) {
  const W = '100%';
  const H = 64;
  return (
    <div
      aria-hidden="true"
      style={{
        width: W,
        height: H,
        borderRadius: 8,
        border: `1px solid ${colors.border}`,
        position: 'relative',
        overflow: 'hidden',
        background: kind === 'dark' ? '#0F1115' : '#F7F8FA',
      }}
    >
      {kind === 'system' ? (
        <>
          <div style={paneStyle('light', 'left')} />
          <div style={paneStyle('dark', 'right')} />
          <Bar t="6px" w="34%" left="8px" tone={kind === 'dark' ? 'dark' : 'light'} />
          <Bar t="20px" w="56%" left="8px" tone="light" />
          <Bar t="6px" w="32%" right="8px" tone="dark" />
          <Bar t="20px" w="48%" right="8px" tone="dark" />
        </>
      ) : (
        <>
          <Bar t="10px" w="56%" left="10px" tone={kind} />
          <Bar t="24px" w="38%" left="10px" tone={kind} />
          <Bar t="42px" w="62%" left="10px" tone={kind} />
        </>
      )}
    </div>
  );
}

function paneStyle(side, alignment) {
  return {
    position: 'absolute',
    top: 0,
    bottom: 0,
    [alignment]: 0,
    width: '50%',
    background: side === 'dark' ? '#0F1115' : '#F7F8FA',
    borderRight: alignment === 'left' ? `1px solid #2A2F3A` : 'none',
  };
}

function Bar({ t, w, left, right, tone }) {
  const isDark = tone === 'dark';
  return (
    <div
      style={{
        position: 'absolute',
        top: t,
        left,
        right,
        width: w,
        height: 6,
        borderRadius: 3,
        background: isDark ? '#2A2F3A' : '#E5E7EB',
      }}
    />
  );
}
